import axios from 'axios';
import buildPaginationQueryOpts from '@/shared/sort/sorts';
import {IDoc} from '@/shared/model/doc/doc.model';
import {IDocWidthResolution} from '@/shared/model/doc/docWidthResolution.model';
import {ITask} from '@/shared/model/resolution/task.model';
import {IReceivedAnswer} from '@/shared/model/resolution/receivedAnswer.model';
import {IDocInternalBadge} from '@/shared/model/doc/docInternalBadge.model';
import {IToDecisiveBadge} from '@/shared/model/resolution/taskReceivedBadge.model';
import {IHelperBadge} from '@/shared/model/doc/helper-badge.model';
import {IQuarter} from '@/shared/model/doc/quarter.model';
import {IMyBadgeCard} from "@/shared/model/dashboard/my-badge-card.model";

const baseApiUrl = 'api/docs';
const baseApiQuarterUrl = 'api/mahalla';

export default class DocService {
  public find(id: number): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findDocShortInfo(id: number): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/short-info/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findDocDefaultInfo(id: number): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/default-info/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findDocDefaultInfoWithResolution(id: number): Promise<IDocWidthResolution> {
    return new Promise<IDocWidthResolution>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/default-info-with-resolution/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findTreeInfo(id: number): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/tree/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findHistoryInfo(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/history/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findTreeInfoLazy(taskId: number): Promise<Array<IDoc>> {
    return new Promise<Array<IDoc>>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/tree/lazy/${taskId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findAllFiles(id: number): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/files/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieve(paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl + `?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findOneWidthResolution(id: number): Promise<IDocWidthResolution> {
    return new Promise<IDocWidthResolution>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/width-resolution/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findOneForUpdate(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/for-update/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/all/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllSignedDocsPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/all-signed-docs/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllIncomeProsecutorLetter(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/all-income-prosecutor-letter/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllPagingQuarter(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiQuarterUrl + `/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllPagingQuarterConfirm(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiQuarterUrl + `/docs/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrievePaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveForCallCenterPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/call-center/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveJusticeQuarterPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/justice-quarter/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveInternalPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/internal/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveNgoDocPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/e-ngo/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getNgoBadges(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      axios
        .get(baseApiUrl + `/e-ngo/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // public retrieveInternalRegistrationPaging(paginationQuery: any, filter: any): Promise<any> {
  //   return new Promise<any>((resolve, reject) => {
  //     axios
  //       .get(baseApiUrl + `/internal-registration/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
  //       .then(res => {
  //         resolve(res);
  //       })
  //       .catch(err => {
  //         reject(err);
  //       });
  //   });
  // }

  public getInternalBadge(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/internal/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getContractBadge(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/contract/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocOutBadge(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/internal/doc-out/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
  public getDocGuaranteeBadge(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/internal/doc-guarantee/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocNoticeBadge(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/internal/doc-notice/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocAppealBadges(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/appeal-tab/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getQuarters(): Promise<IDocInternalBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/appeal-tab/badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public deleteMahallaDoc(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiQuarterUrl}/docs/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public create(entity: IDoc): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  //vaqtincha rad etish yopib turish aytildi. shunga commentga olindi 28.11.2024
  // public rejectMahallaQuarter(entity: any): Promise<any> {
  //   return new Promise<any>((resolve, reject) => {
  //     axios
  //       .post(`${baseApiQuarterUrl}` + '/docs/reject', entity)
  //       .then(res => {
  //         resolve(res.data);
  //       })
  //       .catch(err => {
  //         reject(err);
  //       });
  //   });
  // }

  public createQuarter(entity: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiQuarterUrl}` + '/doc', entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public createLetterFromProsecutor(entity: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}` + '/letter-prosecutor', entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public createWidthResolution(entity: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/width-resolution`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public createWidthResolutionForDocOut(entity: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/width-resolution/internal-out-doc`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public update(entity: IDoc): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateAppealMainFile(docId: Number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/update-appeal-main-file/${docId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateWidthResolution(entity: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/width-resolution`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllList(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl + `/all-list`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findAllStatusList(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl + `/status/list`)
        .then(function (res) {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // public findAllGenderList(): Promise<any> {
  //   return new Promise<any>((resolve, reject) => {
  //     axios
  //       .get(baseApiUrl + `/gender/list`)
  //       .then(function (res) {
  //         resolve(res.data);
  //       })
  //       .catch(err => {
  //         reject(err);
  //       });
  //   });
  // }

  public updateMainFile(docId: Number, newHashId: String): Promise<any> {
    return new Promise<IReceivedAnswer>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/update-main-file/${docId}/${newHashId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateMahallaAppealMainFile(docId: Number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .put(`${baseApiQuarterUrl}/update-appeal-main-file/${docId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findOneInternalResolutionCardInfo(docId: number): Promise<any> {
    return new Promise<ITask>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/internal-resolution-card-info/${docId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateDescription(entity: IDoc): Promise<IDoc> {
    return new Promise<IDoc>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/update/description`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public createDocCheck(entity: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/create/check`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getHelperBadge(): Promise<IHelperBadge> {
    return new Promise<IHelperBadge>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/helper-badge`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocInternalTableBadges(): Promise<IToDecisiveBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}` + '/doc-internal/get-badges')
        .then(res => {
          resolve(res.data.body);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocLetterTableBadges(): Promise<IToDecisiveBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}` + '/helper-letter/get-badges')
        .then(res => {
          resolve(res.data.body);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getDocAppealTableBadges(): Promise<IToDecisiveBadge> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}` + '/helper-appeal/get-badges')
        .then(res => {
          resolve(res.data.body);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  getSigningPersons(signedByValue: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`api/signing-persons?name=${signedByValue}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
